<template>
  <div :style="containerStyle">
    <div :style="fullImageStyle" class="absolute outline outline-blue-400 cursor-move" @pointerdown.stop="startDrag($event, SURFACE_EDITOR_DRAG_HANDLE.all, 'full')">
      <div class="absolute-cover opacity-50">
        <use-image :src="imageUrl" draggable="false" class="object-fill block h-full w-full absolute pointer-events-none" />
      </div>

      <div
        v-for="corner in fullImageDragCorners"
        :key="corner.handle"
        :class="[
          'absolute -translate-x-1/2 -translate-y-1/2 bg-white hover:bg-blue-400 transition-all rounded-full w-4 h-4 border border-blue-950 z-50',
          corner.class,
          {
            'opacity-0': isDragging && (handleType === 'crop' || corner.handle !== activeHandle)
          }
        ]"
        :style="{ cursor: corner.cursor }"
        @pointerdown.stop="startDrag($event, corner.handle, 'full')"
      />
    </div>

    <div :style="cropWindowStyle" class="absolute outline outline-2 outline-blue-400 pointer-events-none">
      <div class="absolute-cover overflow-hidden" :style="cropImageContainerStyle">
        <div :style="fullImageStyle" class="absolute">
          <use-image :src="imageUrl" draggable="false" class="object-fill block h-full w-full absolute pointer-events-none" />
        </div>
      </div>

      <!-- grid on in crop window -->
      <div
        :class="[
          'absolute-cover pointer-events-none transition-opacity',
          {
            'opacity-0': !isDragging,
            'opacity-100': isDragging
          }
        ]"
      >
        <div class="absolute left-1/3 top-0 bottom-0 w-px bg-white" />
        <div class="absolute right-1/3 top-0 bottom-0 w-px bg-white" />
        <div class="absolute top-1/3 left-0 right-0 h-px bg-white" />
        <div class="absolute bottom-1/3 left-0 right-0 h-px bg-white" />
      </div>

      <u-icon
        v-for="corner in cropWindowDragCorners"
        :key="corner.handle"
        :name="corner.icon"
        :class="[
          'absolute -translate-x-1/2 -translate-y-1/2 bg-white hover:bg-blue-400 transition-all z-50 rotate-45 w-8 h-8 pointer-events-auto',
          corner.class,
          {
            'opacity-0': isDragging && (handleType === 'full' || corner.handle !== activeHandle)
          }
        ]"
        :style="{ cursor: corner.cursor }"
        @pointerdown.stop="startDrag($event, corner.handle, 'crop')"
      />
    </div>
  </div>
</template>

<script setup>
  import cloneDeep from 'lodash.clonedeep';
  import {storeToRefs} from 'pinia';
  import {UseImage} from '@vueuse/components';

  const props = defineProps({
    element: Object
  });

  const surfaceEditorStore = useSurfaceEditorStore();
  const {scale} = storeToRefs(surfaceEditorStore);

  const surfaceEditorUtils = useSurfaceEditorUtils();

  //for reset purposes
  const preCropOriginalElement = cloneDeep(props.element);

  //to work locally on and update the store original in debounced manner
  const localElement = ref(cloneDeep(props.element));
  const image = computed(() => localElement.value.content);

  const {imageUrl} = useSurfaceEditorImageRequest({
    image,
    renderScale: computed(() => props.renderScale ?? 1)
  });

  const containerStyle = computed(() => surfaceEditorStore.getScaledElementPositionStyle({element: localElement.value}));

  const fullImageStyle = computed(() => ({
    width: `${image.value.image_width * image.value.scale * scale.value}px`,
    height: `${image.value.image_height * image.value.scale * scale.value}px`,
    transform: `translate(${image.value.offset_x * scale.value}px, ${image.value.offset_y * scale.value}px)`
  }));

  const cropWindowStyle = computed(() => ({
    width: `${localElement.value.width * scale.value}px`,
    height: `${localElement.value.height * scale.value}px`
  }));

  const roundingInPixels = computed(() => surfaceEditorUtils.getRoundingInPixels({
    rounding: image.value.rounding,
    elementWidth: localElement.value.width,
    elementHeight: localElement.value.height
  }));

  const cropImageContainerStyle = computed(() => ({
    borderRadius: `${roundingInPixels.value * scale.value}px`
  }));

  function save() {
    surfaceEditorStore.updateElement({
      element: props.element,
      updates: localElement.value
    });
  }

  // note: when wiring reset, cannot be called from onBeforeUnmount, because at that point we have lost the reference to editedElement
  function reset() {
    localElement.value = preCropOriginalElement;
    save();
  }

  /**
   * Get the appropriate resize cursor based on element rotation.
   * Cursors cycle through 4 directions every 180° (45° per step).
   */
  function getRotatedCursor(baseCursor) {
    const rotation = localElement.value.rotation || 0;
    const normalizedRotation = ((rotation % 180) + 180) % 180;
    const step = Math.round(normalizedRotation / 45) % 4;

    const cursorCycle = ['nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize'];
    const baseIndex = cursorCycle.indexOf(baseCursor);
    if (baseIndex === -1) return baseCursor;

    const newIndex = (baseIndex + step) % 4;
    return cursorCycle[newIndex];
  }

  const fullImageDragCorners = computed(() => [
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.topLeft,
      class: 'top-0 left-0',
      cursor: getRotatedCursor('nwse-resize')
    },
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.topRight,
      class: 'top-0 left-full',
      cursor: getRotatedCursor('nesw-resize')
    },
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.bottomLeft,
      class: 'top-full left-0',
      cursor: getRotatedCursor('nesw-resize')
    },
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.bottomRight,
      class: 'top-full left-full',
      cursor: getRotatedCursor('nwse-resize')
    }
  ]);

  const cropWindowDragCorners = computed(() => [
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.topLeft,
      class: 'top-0 left-0',
      cursor: getRotatedCursor('nwse-resize'),
      icon: 'i-ri-arrow-left-s-line',
    },
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.topRight,
      class: 'top-0 left-full',
      cursor: getRotatedCursor('nesw-resize'),
      icon: 'i-ri-arrow-up-s-line',
    },
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.bottomLeft,
      class: 'top-full left-0',
      cursor: getRotatedCursor('nesw-resize'),
      icon: 'i-ri-arrow-down-s-line',
    },
    {
      handle: SURFACE_EDITOR_DRAG_HANDLE.bottomRight,
      class: 'top-full left-full',
      cursor: getRotatedCursor('nwse-resize'),
      icon: 'i-ri-arrow-right-s-line',
    }
  ]);

  const isDragging = ref(false);
  const activeHandle = ref(null);
  const handleType = ref(null);
  let originalElement = null;
  let originalImage = null;
  const startPos = ref({ x: 0, y: 0 });

  /**
   * Rotates a vector by a given angle (in degrees)
   * Used to transform between screen space and element-local space
   */
  function rotateVector(dx, dy, angleDegrees) {
    if (!angleDegrees) return {dx, dy};
    const angleRad = -angleDegrees * (Math.PI / 180);
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    return {
      dx: dx * cos - dy * sin,
      dy: dx * sin + dy * cos
    };
  }

  /**
   * Calculate new element position to keep an anchor point visually fixed after dimension changes.
   * This is necessary because CSS rotation happens around the element's center, so changing
   * dimensions shifts the center and causes visual movement.
   *
   * @param anchorX - anchor point X in normalized coords (0 = left, 1 = right)
   * @param anchorY - anchor point Y in normalized coords (0 = top, 1 = bottom)
   */
  function calculatePositionForFixedAnchor({
    originalX, originalY, originalWidth, originalHeight,
    newWidth, newHeight,
    anchorX, anchorY,
    rotation
  }) {
    if (!rotation) {
      // No rotation - simple linear calculation
      return {
        x: originalX + anchorX * originalWidth - anchorX * newWidth,
        y: originalY + anchorY * originalHeight - anchorY * newHeight
      };
    }

    const r = rotation * Math.PI / 180;
    const cos = Math.cos(r);
    const sin = Math.sin(r);

    // Calculate original anchor position in screen space
    // Anchor relative to element center (before rotation)
    const origAnchorRelX = (anchorX - 0.5) * originalWidth;
    const origAnchorRelY = (anchorY - 0.5) * originalHeight;

    // Apply rotation to get anchor position relative to center
    const origAnchorRotX = origAnchorRelX * cos - origAnchorRelY * sin;
    const origAnchorRotY = origAnchorRelX * sin + origAnchorRelY * cos;

    // Original center and anchor screen positions
    const origCenterX = originalX + originalWidth / 2;
    const origCenterY = originalY + originalHeight / 2;
    const anchorScreenX = origCenterX + origAnchorRotX;
    const anchorScreenY = origCenterY + origAnchorRotY;

    // Calculate new anchor relative to new center (before rotation)
    const newAnchorRelX = (anchorX - 0.5) * newWidth;
    const newAnchorRelY = (anchorY - 0.5) * newHeight;

    // Apply rotation
    const newAnchorRotX = newAnchorRelX * cos - newAnchorRelY * sin;
    const newAnchorRotY = newAnchorRelX * sin + newAnchorRelY * cos;

    // Solve for new position: newCenter + newAnchorRot = anchorScreen
    // newX + newWidth/2 + newAnchorRotX = anchorScreenX
    return {
      x: anchorScreenX - newWidth / 2 - newAnchorRotX,
      y: anchorScreenY - newHeight / 2 - newAnchorRotY
    };
  }

  const startDrag = (event, handle, type) => {
    if (event.button !== 0) {
      return; //not left click
    }

    // Capture pointer so drag continues even if pointer leaves the element
    event.target.setPointerCapture(event.pointerId);

    isDragging.value = true;
    activeHandle.value = handle;
    handleType.value = type;
    startPos.value = {
      x: event.clientX,
      y: event.clientY
    };
    originalElement = {
      x: localElement.value.x,
      y: localElement.value.y,
      width: localElement.value.width,
      height: localElement.value.height,
      rotation: localElement.value.rotation || 0
    };
    originalImage = {
      offset_x: image.value.offset_x,
      offset_y: image.value.offset_y,
      image_width: image.value.image_width,
      image_height: image.value.image_height,
      scale: image.value.scale
    };

    window.addEventListener('pointermove', onDrag);
    window.addEventListener('pointerup', stopDrag);
  }

  const onDrag = (event) => {
    if (!isDragging.value) {
      return;
    }

    // Get screen-space deltas
    const screenDx = (event.clientX - startPos.value.x) / scale.value;
    const screenDy = (event.clientY - startPos.value.y) / scale.value;

    // Transform to element-local space if rotated
    const rotation = originalElement.rotation;
    const local = rotation ? rotateVector(screenDx, screenDy, rotation) : {dx: screenDx, dy: screenDy};

    if (handleType.value === 'full') {
      morphFull({dx: local.dx, dy: local.dy});
    } else {
      morphCrop({dx: local.dx, dy: local.dy});
    }
  }

  const stopDrag = () => {
    if (!isDragging.value) {
      return;
    }

    isDragging.value = false;
    activeHandle.value = null;
    handleType.value = null;
    originalElement = null;
    originalImage = null;

    window.removeEventListener('pointermove', onDrag);
    window.removeEventListener('pointerup', stopDrag);
  }

  //morph handlers
  function morphFull({dx, dy}) {
    let minDx, maxDx, minDy, maxDy, boundedDx, boundedDy;
    let scaleX, scaleY, dominantScale, newWidth, newHeight;

    switch (activeHandle.value) {
      case SURFACE_EDITOR_DRAG_HANDLE.all:
        minDx = -(((originalImage.image_width * originalImage.scale) - originalElement.width) + originalImage.offset_x);
        maxDx = -originalImage.offset_x;
        boundedDx = Math.max(Math.min(dx, maxDx), minDx);
        minDy = -(((originalImage.image_height * originalImage.scale) - originalElement.height) + originalImage.offset_y);
        maxDy = -originalImage.offset_y;
        boundedDy = Math.max(Math.min(dy, maxDy), minDy);

        image.value.offset_x = Math.round(originalImage.offset_x + boundedDx);
        image.value.offset_y = Math.round(originalImage.offset_y + boundedDy);

        break;
      case SURFACE_EDITOR_DRAG_HANDLE.topLeft:
        maxDx = -originalImage.offset_x;
        boundedDx = Math.min(dx, maxDx);
        maxDy = -originalImage.offset_y;
        boundedDy = Math.min(dy, maxDy);

        scaleX = ((originalImage.image_width * originalImage.scale) - boundedDx) / originalImage.image_width;
        scaleY = ((originalImage.image_height * originalImage.scale) - boundedDy) / originalImage.image_height;
        dominantScale = (scaleX - 1) > (scaleY - 1)
          ? scaleX
          : scaleY;

        newWidth = Math.round(originalImage.image_width * dominantScale);
        newHeight = Math.round(originalImage.image_height * dominantScale);

        image.value.scale = dominantScale;
        image.value.offset_x = Math.round(originalImage.offset_x + ((originalImage.image_width * originalImage.scale) - newWidth));
        image.value.offset_y = Math.round(originalImage.offset_y + ((originalImage.image_height * originalImage.scale) - newHeight));

        break;

      case SURFACE_EDITOR_DRAG_HANDLE.topRight:
        minDx = -(((originalImage.image_width * originalImage.scale) - originalElement.width) + originalImage.offset_x);
        boundedDx = Math.max(dx, minDx);
        maxDy = -originalImage.offset_y;
        boundedDy = Math.min(dy, maxDy);

        scaleX = ((originalImage.image_width * originalImage.scale) + boundedDx) / originalImage.image_width;
        scaleY = ((originalImage.image_height * originalImage.scale) - boundedDy) / originalImage.image_height;
        dominantScale = (scaleX - 1) > (scaleY - 1)
          ? scaleX
          : scaleY;

        newWidth = Math.round(originalImage.image_width * dominantScale);
        newHeight = Math.round(originalImage.image_height * dominantScale);

        image.value.scale = dominantScale;
        image.value.offset_y = Math.round(originalImage.offset_y + ((originalImage.image_height * originalImage.scale) - newHeight));

        break;

      case SURFACE_EDITOR_DRAG_HANDLE.bottomLeft:
        maxDx = -originalImage.offset_x;
        boundedDx = Math.min(dx, maxDx);
        minDy = -(((originalImage.image_height * originalImage.scale) - originalElement.height) + originalImage.offset_y);
        boundedDy = Math.max(dy, minDy);

        scaleX = ((originalImage.image_width * originalImage.scale) - boundedDx) / originalImage.image_width;
        scaleY = ((originalImage.image_height * originalImage.scale) + boundedDy) / originalImage.image_height;
        dominantScale = (scaleX - 1) > (scaleY - 1)
          ? scaleX
          : scaleY;

        newWidth = Math.round(originalImage.image_width * dominantScale);
        newHeight = Math.round(originalImage.image_height * dominantScale);

        image.value.scale = dominantScale;
        image.value.offset_x = Math.round(originalImage.offset_x + ((originalImage.image_width * originalImage.scale) - newWidth));

        break;

      case SURFACE_EDITOR_DRAG_HANDLE.bottomRight:
        minDx = -(((originalImage.image_width * originalImage.scale) - originalElement.width) + originalImage.offset_x);
        boundedDx = Math.max(dx, minDx);
        minDy = -(((originalImage.image_height * originalImage.scale) - originalElement.height) + originalImage.offset_y);
        boundedDy = Math.max(dy, minDy);

        scaleX = ((originalImage.image_width * originalImage.scale) + boundedDx) / originalImage.image_width;
        scaleY = ((originalImage.image_height * originalImage.scale) + boundedDy) / originalImage.image_height;
        dominantScale = (scaleX - 1) > (scaleY - 1)
          ? scaleX
          : scaleY;

        newWidth = Math.round(originalImage.image_width * dominantScale);
        newHeight = Math.round(originalImage.image_height * dominantScale);

        image.value.scale = dominantScale;
    }
  }

  function morphCrop({dx, dy}) {
    let minDx, maxDx, minDy, maxDy, boundedDx, boundedDy;
    const rotation = originalElement.rotation;
    let newWidth, newHeight, newPos;

    switch (activeHandle.value) {
      case SURFACE_EDITOR_DRAG_HANDLE.topLeft:
        // Anchor: bottom-right (1, 1)
        minDx = originalImage.offset_x;
        maxDx = originalElement.width - SURFACE_EDITOR_MIN_ELEMENT_DIMENSION;
        boundedDx = Math.max(Math.min(dx, maxDx), minDx);
        minDy = originalImage.offset_y;
        maxDy = originalElement.height - SURFACE_EDITOR_MIN_ELEMENT_DIMENSION;
        boundedDy = Math.max(Math.min(dy, maxDy), minDy);

        newWidth = Math.round(originalElement.width - boundedDx);
        newHeight = Math.round(originalElement.height - boundedDy);

        localElement.value.width = newWidth;
        localElement.value.height = newHeight;
        image.value.offset_x = Math.round(originalImage.offset_x - boundedDx);
        image.value.offset_y = Math.round(originalImage.offset_y - boundedDy);

        // Calculate position to keep bottom-right corner fixed
        newPos = calculatePositionForFixedAnchor({
          originalX: originalElement.x,
          originalY: originalElement.y,
          originalWidth: originalElement.width,
          originalHeight: originalElement.height,
          newWidth,
          newHeight,
          anchorX: 1,
          anchorY: 1,
          rotation
        });
        localElement.value.x = Math.round(newPos.x);
        localElement.value.y = Math.round(newPos.y);

        break;

      case SURFACE_EDITOR_DRAG_HANDLE.topRight:
        // Anchor: bottom-left (0, 1)
        minDx = -originalElement.width + SURFACE_EDITOR_MIN_ELEMENT_DIMENSION;
        maxDx = ((originalImage.image_width * originalImage.scale) - originalElement.width) + originalImage.offset_x;
        boundedDx = Math.max(Math.min(dx, maxDx), minDx);
        minDy = originalImage.offset_y;
        maxDy = originalElement.height - SURFACE_EDITOR_MIN_ELEMENT_DIMENSION;
        boundedDy = Math.max(Math.min(dy, maxDy), minDy);

        newWidth = Math.round(originalElement.width + boundedDx);
        newHeight = Math.round(originalElement.height - boundedDy);

        localElement.value.width = newWidth;
        localElement.value.height = newHeight;
        image.value.offset_y = Math.round(originalImage.offset_y - boundedDy);

        // Calculate position to keep bottom-left corner fixed
        newPos = calculatePositionForFixedAnchor({
          originalX: originalElement.x,
          originalY: originalElement.y,
          originalWidth: originalElement.width,
          originalHeight: originalElement.height,
          newWidth,
          newHeight,
          anchorX: 0,
          anchorY: 1,
          rotation
        });
        localElement.value.x = Math.round(newPos.x);
        localElement.value.y = Math.round(newPos.y);

        break;

      case SURFACE_EDITOR_DRAG_HANDLE.bottomLeft:
        // Anchor: top-right (1, 0)
        minDx = originalImage.offset_x;
        maxDx = originalElement.width - SURFACE_EDITOR_MIN_ELEMENT_DIMENSION;
        boundedDx = Math.max(Math.min(dx, maxDx), minDx);
        minDy = -(originalElement.height + SURFACE_EDITOR_MIN_ELEMENT_DIMENSION);
        maxDy = ((originalImage.image_height * originalImage.scale) - originalElement.height) + originalImage.offset_y;
        boundedDy = Math.max(Math.min(dy, maxDy), minDy);

        newWidth = Math.round(originalElement.width - boundedDx);
        newHeight = Math.round(originalElement.height + boundedDy);

        localElement.value.width = newWidth;
        localElement.value.height = newHeight;
        image.value.offset_x = Math.round(originalImage.offset_x - boundedDx);

        // Calculate position to keep top-right corner fixed
        newPos = calculatePositionForFixedAnchor({
          originalX: originalElement.x,
          originalY: originalElement.y,
          originalWidth: originalElement.width,
          originalHeight: originalElement.height,
          newWidth,
          newHeight,
          anchorX: 1,
          anchorY: 0,
          rotation
        });
        localElement.value.x = Math.round(newPos.x);
        localElement.value.y = Math.round(newPos.y);

        break;

      case SURFACE_EDITOR_DRAG_HANDLE.bottomRight:
        // Anchor: top-left (0, 0)
        minDx = -originalElement.width + SURFACE_EDITOR_MIN_ELEMENT_DIMENSION;
        maxDx = ((originalImage.image_width * originalImage.scale) - originalElement.width) + originalImage.offset_x;
        boundedDx = Math.max(Math.min(dx, maxDx), minDx);
        minDy = -(originalElement.height + SURFACE_EDITOR_MIN_ELEMENT_DIMENSION);
        maxDy = ((originalImage.image_height * originalImage.scale) - originalElement.height) + originalImage.offset_y;
        boundedDy = Math.max(Math.min(dy, maxDy), minDy);

        newWidth = Math.round(originalElement.width + boundedDx);
        newHeight = Math.round(originalElement.height + boundedDy);

        localElement.value.width = newWidth;
        localElement.value.height = newHeight;

        // Calculate position to keep top-left corner fixed
        newPos = calculatePositionForFixedAnchor({
          originalX: originalElement.x,
          originalY: originalElement.y,
          originalWidth: originalElement.width,
          originalHeight: originalElement.height,
          newWidth,
          newHeight,
          anchorX: 0,
          anchorY: 0,
          rotation
        });
        localElement.value.x = Math.round(newPos.x);
        localElement.value.y = Math.round(newPos.y);

        break;
    }
  }

  onBeforeUnmount(() => {
    stopDrag();
    save();
  });

</script>
